import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, cn, DEPARTMENT_COLORS, DEPARTMENT_LABELS } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { TrendingUp, TrendingDown, BarChart3, Target, Clock, CheckCircle2 } from "lucide-react";

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportRes, deptRes] = await Promise.all([
          axios.get(`${API}/reports/performance?days=${period}`, { withCredentials: true }),
          axios.get(`${API}/dashboard/tasks-by-department`, { withCredentials: true }),
        ]);
        setReport(reportRes.data);
        setDepartmentStats(deptRes.data);
      } catch (err) {
        console.error("Failed to fetch reports:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]);

  if (loading) {
    return <ReportsSkeleton />;
  }

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1">
            Performance analytics and insights
          </p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px] rounded-sm" data-testid="period-select">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  On-Time Rate
                </p>
                <p className="metric-value mt-2 text-emerald-500">{report?.on_time_rate || 0}%</p>
              </div>
              <div className="p-2 rounded-sm bg-emerald-50">
                <Target className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
            <Progress value={report?.on_time_rate || 0} className="mt-3 h-1" />
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Completed
                </p>
                <p className="metric-value mt-2">{report?.total_completed || 0}</p>
              </div>
              <div className="p-2 rounded-sm bg-blue-50">
                <CheckCircle2 className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              in the last {period} days
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  On Time
                </p>
                <p className="metric-value mt-2 text-emerald-500">
                  {report?.completed_on_time || 0}
                </p>
              </div>
              <div className="p-2 rounded-sm bg-emerald-50">
                <TrendingUp className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Late
                </p>
                <p className="metric-value mt-2 text-rose-500">
                  {report?.completed_late || 0}
                </p>
              </div>
              <div className="p-2 rounded-sm bg-rose-50">
                <TrendingDown className="w-5 h-5 text-rose-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Performance */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Department Performance
          </CardTitle>
          <CardDescription>Task completion status by department</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {departmentStats.map((dept) => {
              const colors = DEPARTMENT_COLORS[dept.department] || DEPARTMENT_COLORS.merchandizing;
              const completionRate = dept.total > 0 ? Math.round((dept.completed / dept.total) * 100) : 0;

              return (
                <div key={dept.department} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-3 h-3 rounded-full", colors.bg)} />
                      <span className="font-medium capitalize">
                        {DEPARTMENT_LABELS[dept.department] || dept.department}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="secondary" className="font-mono text-xs">
                        {dept.total} total
                      </Badge>
                      <span className="font-mono text-sm font-medium">
                        {completionRate}%
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center p-2 bg-muted/30 rounded-sm">
                      <p className="text-lg font-heading">{dept.pending}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                    <div className="text-center p-2 bg-muted/30 rounded-sm">
                      <p className="text-lg font-heading text-blue-500">{dept.in_progress}</p>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                    <div className="text-center p-2 bg-muted/30 rounded-sm">
                      <p className="text-lg font-heading text-emerald-500">{dept.completed}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center p-2 bg-muted/30 rounded-sm">
                      <p className="text-lg font-heading">{completionRate}%</p>
                      <p className="text-xs text-muted-foreground">Rate</p>
                    </div>
                  </div>
                  <Progress value={completionRate} className="h-1.5" />
                </div>
              );
            })}

            {departmentStats.length === 0 && (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No department data available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Summary Note */}
      <Card className="border border-border shadow-none rounded-sm bg-muted/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-sm bg-amber-50">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-heading text-lg">Weekly Performance Reports</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Automated weekly performance reports will be sent via Email and WhatsApp every Monday.
                This feature tracks on-time completion rates for all team members.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const ReportsSkeleton = () => (
  <div className="space-y-8">
    <div className="flex justify-between items-center">
      <Skeleton className="h-10 w-32" />
      <Skeleton className="h-10 w-[150px]" />
    </div>
    <div className="grid grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-28" />
      ))}
    </div>
    <Skeleton className="h-96" />
  </div>
);
